import React, { useState } from "react";
import styled from "@emotion/styled";
import ParameterTags from "../../../../components/Configurations/ParameterTags";
import { useSelector } from "react-redux";
import LeadTagPerformance from "../../../../components/Configurations/LeadTagPerformance";
import ParameterPerformanceStats from "../../../../components/Configurations/ParameterPerformanceStats";
import { selectMortgageDataModel } from "../../../../slices/mortgageDataModelSlice";

const configurationViews = [
  "Field Performance",
  "Investigation Tags",
  "Tag Performance",
]

const Configuration = () => {
  const mortgageDataModel = useSelector(selectMortgageDataModel)

  const [configurationView, seetConfigurationView] = useState('Field Performance')

  const handleConfigurationViewChange = (event) => {
    seetConfigurationView(event.target.value)
  }

  return (
    <ConfigurationPage>
      <ConfigurationWorkspace>
        <WorkspaceHeader>
          <div>
            <WorkspaceEyebrow>Configuration</WorkspaceEyebrow>
            <WorkspaceTitle>Configuration Analytics</WorkspaceTitle>
          </div>
          <WorkspaceSummary>
            Review sweep field performance, investigation tag assignment, and tag outcomes across portfolio monitoring.
          </WorkspaceSummary>
        </WorkspaceHeader>

        <ViewSelector aria-label="Configuration tables">
          {configurationViews.map((view) => (
            <ViewButton
              key={view}
              type="button"
              value={view}
              className={configurationView === view ? "active" : ""}
              aria-pressed={configurationView === view}
              onClick={handleConfigurationViewChange}
            >
              {view}
            </ViewButton>
          ))}
        </ViewSelector>

        <WorkspaceScroll>
          {configurationView === 'Field Performance' ?
            <ParameterPerformanceStats mortgageDataModel={mortgageDataModel} />
          : configurationView === 'Investigation Tags' ?
            <ParameterTags mortgageDataModel={mortgageDataModel} />
          :
            <LeadTagPerformance mortgageDataModel={mortgageDataModel} />
          }
        </WorkspaceScroll>
      </ConfigurationWorkspace>
    </ConfigurationPage>
  )
}

const ConfigurationPage = styled.main(({ theme }) => `
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  padding: 1rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const ConfigurationWorkspace = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  justify-self: stretch;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.9rem;
  background: ${theme.palette.common.cardBackground};
  box-shadow: inset 0 0.1rem 0 rgba(255, 255, 255, 0.72);
`)

const WorkspaceHeader = styled.header(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.45rem;
  padding: 1.35rem 1.5rem 1.1rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const WorkspaceEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.25rem;
  color: ${theme.palette.primary.main};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
`)

const WorkspaceTitle = styled.h1(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.15;
`)

const WorkspaceSummary = styled.p(({ theme }) => `
  max-width: 58rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.96rem;
  font-weight: 550;
  line-height: 1.45;
`)

const ViewSelector = styled.div(({ theme }) => `
  min-width: 0;
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  padding: 0.85rem 1.1rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const ViewButton = styled.button(({ theme }) => `
  min-height: 2.35rem;
  padding: 0.42rem 0.85rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.7rem;
  background: ${theme.palette.common.white};
  color: ${theme.palette.common.grayText};
  font-size: 0.88rem;
  font-weight: 750;
  line-height: 1.2;
  cursor: pointer;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    color: ${theme.palette.common.black};
    border-color: ${theme.palette.common.grayBorderDark};
    background: ${theme.palette.common.cardBackground};
  }

  &:focus-visible {
    outline: 0.18rem solid ${theme.palette.primary.labelTransparent};
    outline-offset: 0.12rem;
  }

  &.active {
    border-color: ${theme.palette.primary.main};
    background: ${theme.palette.primary.labelTransparent};
    color: ${theme.palette.primary.dark};
    box-shadow: inset 0.22rem 0 0 ${theme.palette.primary.main};
  }
`)

const WorkspaceScroll = styled.div(({ theme }) => `
  min-height: 0;
  height: 100%;
  max-height: 100%;
  display: grid;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 1.1rem;
`)

export default Configuration
